import {
  uuidv7,
  type Event,
  type EventParticipant,
  type List,
  type ListItem,
  type Message,
  type Place,
  type Tag,
  type TagLink,
  type Topic,
  type Wish,
} from "@vmeste/shared";
import type { Operation, QueuedOperation } from "./outbox.js";
import {
  eventParticipantKey,
  placeReviewKey,
  reactionKey,
  tagLinkKey,
  type SpaceState,
} from "./store.js";

/**
 * Unsent changes, layered over synced state.
 *
 * The alternative — writing optimistically into the base state — loses edits:
 * the next delta overwrites the row and the person's change silently reverts
 * while it is still sitting in the outbox. Keeping pending work as an overlay
 * means a change stays visible until the server confirms it, and disappears
 * from the overlay only when the confirming delta arrives.
 */

/** A provisional row, so the UI can show a create that has not been sent yet. */
function provisionalList(
  spaceId: string,
  operation: Extract<Operation, { kind: "list.create" }>,
  self: string,
  now: string,
): List {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    kind: operation.body.kind,
    content: operation.body.content,
    position: operation.body.position ?? "a0",
    archivedAt: null,
    createdBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    // Below every real revision, so the first server delta for this row wins.
    revision: 0,
  };
}

function provisionalItem(
  spaceId: string,
  operation: Extract<Operation, { kind: "item.create" }>,
  self: string,
  now: string,
): ListItem {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    listId: operation.body.listId,
    parentId: operation.body.parentId ?? null,
    content: operation.body.content,
    done: false,
    doneBy: null,
    doneAt: null,
    dueAt: operation.body.dueAt ?? null,
    assigneeId: operation.body.assigneeId ?? null,
    position: operation.body.position ?? "a0",
    createdBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    revision: 0,
  };
}

function provisionalWish(
  spaceId: string,
  operation: Extract<Operation, { kind: "wish.create" }>,
  self: string,
  now: string,
): Wish {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    content: operation.body.content,
    status: operation.body.status,
    position: operation.body.position ?? "a0",
    claimedBy: null,
    claimedAt: null,
    addedBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    revision: 0,
  };
}

function provisionalPlace(
  spaceId: string,
  operation: Extract<Operation, { kind: "place.create" }>,
  self: string,
  now: string,
): Place {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    content: operation.body.content,
    visitedAt: operation.body.visited ? now : null,
    createdBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    revision: 0,
  };
}

function provisionalTopic(
  spaceId: string,
  operation: Extract<Operation, { kind: "topic.create" }>,
  self: string,
  now: string,
): Topic {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    kind: operation.body.kind,
    // Both or neither: a target half-set would render as a discussion attached
    // to nothing until the real row came back and corrected it.
    targetType: operation.body.targetType ?? null,
    targetId: operation.body.targetId ?? null,
    content: operation.body.content,
    status: "open",
    // A thread starts unpinned; pinning it is a second act, and it arrives as
    // its own update.
    pinnedAt: null,
    pinnedOrder: null,
    createdBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    revision: 0,
  };
}

function provisionalMessage(
  spaceId: string,
  operation: Extract<Operation, { kind: "message.create" }>,
  self: string,
  now: string,
): Message {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    topicId: operation.body.topicId,
    replyToId: operation.body.replyToId ?? null,
    content: operation.body.content,
    authorId: self,
    createdAt: now,
    editedAt: null,
    deletedAt: null,
    revision: 0,
  };
}

function provisionalEvent(
  spaceId: string,
  operation: Extract<Operation, { kind: "event.create" }>,
  self: string,
  now: string,
): Event {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    content: operation.body.content,
    startsAt: operation.body.startsAt,
    endsAt: operation.body.endsAt ?? null,
    allDay: operation.body.allDay,
    reminderLeadMinutes: operation.body.reminderLeadMinutes ?? null,
    rrule: operation.body.rrule ?? null,
    createdBy: self,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    revision: 0,
  };
}

/**
 * The creator's own row, alongside a provisional event.
 *
 * The server writes exactly this when the create lands, so the overlay says the
 * same thing in the meantime — otherwise an event you just made would show
 * nobody in it until the pull came back, which reads as the bug this feature
 * exists to fix.
 */
function provisionalParticipant(
  spaceId: string,
  eventId: string,
  userId: string,
  status: EventParticipant["status"],
  now: string,
): EventParticipant {
  return {
    spaceId,
    eventId,
    userId,
    status,
    updatedAt: now,
    deletedAt: null,
    // Below every real revision, so the first server delta for this row wins.
    revision: 0,
  };
}

function provisionalTag(
  spaceId: string,
  operation: Extract<Operation, { kind: "tag.create" }>,
  self: string,
  now: string,
): Tag {
  return {
    id: operation.body.id ?? operation.id,
    spaceId,
    content: operation.body.content,
    color: operation.body.color ?? null,
    createdBy: self,
    createdAt: now,
    deletedAt: null,
    revision: 0,
  };
}

export interface PendingOptions {
  self: string;
  now?: string;
}

/**
 * Apply every queued operation to a copy of the state, in queue order.
 *
 * Order matters here for the same reason it matters when draining: a create
 * followed by an update has to be read in that sequence or the update lands on
 * nothing.
 */
export function applyPending(
  state: SpaceState,
  queue: readonly QueuedOperation[],
  options: PendingOptions,
): SpaceState {
  if (queue.length === 0) return state;

  const now = options.now ?? new Date().toISOString();
  const lists = { ...state.lists };
  const items = { ...state.items };
  const wishes = { ...state.wishes };
  const places = { ...state.places };
  const placeReviews = { ...state.placeReviews };
  const topics = { ...state.topics };
  const messages = { ...state.messages };
  const events = { ...state.events };
  const eventParticipants = { ...state.eventParticipants };
  const reactions = { ...state.reactions };
  const tags = { ...state.tags };
  const tagLinks = { ...state.tagLinks };

  for (const { operation } of queue) {
    if (operation.spaceId !== state.spaceId) continue;

    switch (operation.kind) {
      case "list.create": {
        const row = provisionalList(state.spaceId, operation, options.self, now);
        // If the server already confirmed this row, its version is better.
        lists[row.id] ??= row;
        break;
      }

      case "list.update": {
        const existing = lists[operation.listId];
        if (!existing) break;
        lists[operation.listId] = {
          ...existing,
          ...(operation.body.content !== undefined
            ? { content: operation.body.content }
            : {}),
          ...(operation.body.position !== undefined
            ? { position: operation.body.position }
            : {}),
          ...(operation.body.archived !== undefined
            ? { archivedAt: operation.body.archived ? now : null }
            : {}),
          updatedAt: now,
        };
        break;
      }

      case "list.delete": {
        delete lists[operation.listId];
        // A list disappearing takes its items with it, exactly as the server
        // will do, so the two views do not disagree while the request is in
        // flight.
        for (const item of Object.values(items)) {
          if (item.listId === operation.listId) delete items[item.id];
        }
        break;
      }

      case "item.create": {
        const row = provisionalItem(state.spaceId, operation, options.self, now);
        items[row.id] ??= row;
        break;
      }

      case "item.update": {
        const existing = items[operation.itemId];
        if (!existing) break;
        const body = operation.body;
        items[operation.itemId] = {
          ...existing,
          ...(body.content !== undefined ? { content: body.content } : {}),
          ...(body.listId !== undefined ? { listId: body.listId } : {}),
          ...(body.parentId !== undefined ? { parentId: body.parentId ?? null } : {}),
          ...(body.position !== undefined ? { position: body.position } : {}),
          ...(body.dueAt !== undefined ? { dueAt: body.dueAt ?? null } : {}),
          ...(body.assigneeId !== undefined
            ? { assigneeId: body.assigneeId ?? null }
            : {}),
          ...(body.done !== undefined
            ? {
                done: body.done,
                // Mirrors what the server does: it owns who ticked it, and a
                // client cannot claim somebody else did.
                doneBy: body.done ? options.self : null,
                doneAt: body.done ? now : null,
              }
            : {}),
          updatedAt: now,
        };
        break;
      }

      case "item.delete": {
        delete items[operation.itemId];
        for (const item of Object.values(items)) {
          if (item.parentId === operation.itemId) delete items[item.id];
        }
        break;
      }

      case "wish.create": {
        const row = provisionalWish(state.spaceId, operation, options.self, now);
        wishes[row.id] ??= row;
        break;
      }

      case "wish.update": {
        const existing = wishes[operation.wishId];
        if (!existing) break;
        const body = operation.body;
        wishes[operation.wishId] = {
          ...existing,
          ...(body.content !== undefined ? { content: body.content } : {}),
          ...(body.status !== undefined ? { status: body.status } : {}),
          ...(body.position !== undefined ? { position: body.position } : {}),
          ...(body.claimed !== undefined
            ? {
                // The server owns these, exactly as it owns doneBy/doneAt; the
                // overlay only mirrors what it will write.
                claimedBy: body.claimed ? options.self : null,
                claimedAt: body.claimed ? now : null,
              }
            : {}),
          updatedAt: now,
        };
        break;
      }

      case "wish.delete": {
        delete wishes[operation.wishId];
        break;
      }

      case "place.create": {
        const row = provisionalPlace(state.spaceId, operation, options.self, now);
        places[row.id] ??= row;
        break;
      }

      case "place.update": {
        const existing = places[operation.placeId];
        if (!existing) break;
        const body = operation.body;
        places[operation.placeId] = {
          ...existing,
          ...(body.content !== undefined ? { content: body.content } : {}),
          // The date is the server's to write, and it only writes it once —
          // the overlay mirrors that rather than inventing a second evening.
          ...(body.visited !== undefined
            ? { visitedAt: body.visited ? (existing.visitedAt ?? now) : null }
            : {}),
          updatedAt: now,
        };
        break;
      }

      case "place.delete": {
        delete places[operation.placeId];
        // The route takes the opinions with it under the same revision, and so
        // does this: a review of a place that is no longer on the map would
        // otherwise sit in the overlay until the next pull.
        for (const key of Object.keys(placeReviews)) {
          if (placeReviews[key]!.placeId === operation.placeId) delete placeReviews[key];
        }
        break;
      }

      case "place.review": {
        /*
         * Always `options.self` — the request names no person, so there is no
         * shape in which this could be somebody else's opinion.
         */
        const key = placeReviewKey({ placeId: operation.placeId, userId: options.self });
        const existing = placeReviews[key];
        placeReviews[key] = {
          // The server mints this one; a provisional row that never reaches
          // the re-encryption pass can carry the operation's own.
          id: existing?.id ?? operation.id,
          spaceId: state.spaceId,
          placeId: operation.placeId,
          userId: options.self,
          rating: operation.body.rating,
          // Omitted means «leave what I wrote», which for a row that is not
          // there yet is nothing at all.
          content:
            operation.body.content !== undefined
              ? operation.body.content
              : (existing?.content ?? null),
          updatedAt: now,
          deletedAt: null,
          revision: existing?.revision ?? 0,
        };
        break;
      }

      case "place.unreview": {
        delete placeReviews[placeReviewKey({ placeId: operation.placeId, userId: options.self })];
        break;
      }

      case "topic.create": {
        const row = provisionalTopic(state.spaceId, operation, options.self, now);
        topics[row.id] ??= row;
        break;
      }

      case "topic.update": {
        const existing = topics[operation.topicId];
        if (!existing) break;
        const body = operation.body;
        topics[operation.topicId] = {
          ...existing,
          ...(body.content !== undefined ? { content: body.content } : {}),
          ...(body.status !== undefined ? { status: body.status } : {}),
          /*
           * The overlay mirrors what the route will write, and nothing more: the
           * moment of pinning belongs to the server, exactly as `claimedAt` does
           * on a wish, and unpinning clears the pair together.
           *
           * A bare `pinnedOrder` — a drag between two pins — moves a topic that
           * is already pinned, so it leaves `pinnedAt` alone.
           */
          ...(body.pinned === true
            ? {
                pinnedAt: existing.pinnedAt ?? now,
                pinnedOrder: body.pinnedOrder ?? existing.pinnedOrder,
              }
            : body.pinned === false
              ? { pinnedAt: null, pinnedOrder: null }
              : body.pinnedOrder !== undefined && existing.pinnedAt !== null
                ? { pinnedOrder: body.pinnedOrder }
                : {}),
          updatedAt: now,
        };
        break;
      }

      case "topic.delete": {
        delete topics[operation.topicId];
        // Messages go with the conversation, as the server will do.
        for (const message of Object.values(messages)) {
          if (message.topicId === operation.topicId) delete messages[message.id];
        }
        break;
      }

      case "message.create": {
        const row = provisionalMessage(state.spaceId, operation, options.self, now);
        messages[row.id] ??= row;
        break;
      }

      case "message.update": {
        const existing = messages[operation.messageId];
        if (!existing) break;
        messages[operation.messageId] = {
          ...existing,
          content: operation.body.content,
          editedAt: now,
        };
        break;
      }

      case "message.delete": {
        delete messages[operation.messageId];
        // The faces go with the bubble, exactly as the server's delete does to
        // them, so the two views do not disagree while the request is in flight.
        for (const [key, row] of Object.entries(reactions)) {
          if (row.messageId === operation.messageId) delete reactions[key];
        }
        break;
      }

      case "event.create": {
        const row = provisionalEvent(state.spaceId, operation, options.self, now);
        events[row.id] ??= row;
        // Whoever made it is in it — the same row the server writes inside the
        // create's own transaction, mirrored here so the sheet does not show an
        // empty event for as long as the queue takes to drain.
        const going = provisionalParticipant(
          state.spaceId,
          row.id,
          options.self,
          "going",
          now,
        );
        eventParticipants[eventParticipantKey(going)] ??= going;
        break;
      }

      case "event.update": {
        const existing = events[operation.eventId];
        if (!existing) break;
        const body = operation.body;
        events[operation.eventId] = {
          ...existing,
          ...(body.content !== undefined ? { content: body.content } : {}),
          ...(body.startsAt !== undefined ? { startsAt: body.startsAt } : {}),
          ...(body.endsAt !== undefined ? { endsAt: body.endsAt ?? null } : {}),
          ...(body.allDay !== undefined ? { allDay: body.allDay } : {}),
          ...(body.rrule !== undefined ? { rrule: body.rrule ?? null } : {}),
          updatedAt: now,
        };
        break;
      }

      case "event.delete": {
        delete events[operation.eventId];
        // The answers go with the event, exactly as the server's delete does to
        // them, so the two views do not disagree while the request is in flight.
        for (const [key, row] of Object.entries(eventParticipants)) {
          if (row.eventId === operation.eventId) delete eventParticipants[key];
        }
        break;
      }

      case "message.react": {
        /*
         * Always `options.self`, for the same reason as the answer below: the
         * payload names no person, so there is no shape in which this could be
         * somebody else's reaction.
         *
         * A reaction is the one gesture in the app whose whole worth is being
         * instant — you tap a face and it is there. Waiting for the round trip
         * would make the tap feel broken on exactly the connections this app is
         * built for.
         */
        const key = reactionKey({ messageId: operation.messageId, userId: options.self });

        if (operation.body.emoji === null) {
          delete reactions[key];
          break;
        }

        const existing = reactions[key];
        reactions[key] = existing
          ? { ...existing, emoji: operation.body.emoji, updatedAt: now }
          : {
              spaceId: state.spaceId,
              messageId: operation.messageId,
              userId: options.self,
              emoji: operation.body.emoji,
              updatedAt: now,
              deletedAt: null,
              // Below every real revision, so the first server delta wins.
              revision: 0,
            };
        break;
      }

      case "event.participate": {
        /*
         * Always `options.self`, never a person named in the payload — because
         * there is none. This is the overlay's half of the same rule the route
         * enforces: a queued answer is your own answer, and there is no shape in
         * which it could be somebody else's.
         */
        const key = eventParticipantKey({
          eventId: operation.eventId,
          userId: options.self,
        });
        const existing = eventParticipants[key];
        eventParticipants[key] = existing
          ? { ...existing, status: operation.body.status, updatedAt: now }
          : provisionalParticipant(
              state.spaceId,
              operation.eventId,
              options.self,
              operation.body.status,
              now,
            );
        break;
      }

      case "tag.create": {
        const row = provisionalTag(state.spaceId, operation, options.self, now);
        tags[row.id] ??= row;
        break;
      }

      case "tag.update": {
        const existing = tags[operation.tagId];
        if (!existing) break;
        tags[operation.tagId] = {
          ...existing,
          ...(operation.body.content !== undefined
            ? { content: operation.body.content }
            : {}),
          ...(operation.body.color !== undefined
            ? { color: operation.body.color ?? null }
            : {}),
        };
        break;
      }

      case "tag.delete": {
        delete tags[operation.tagId];
        // A deleted tag takes its links with it, as the server will.
        for (const [key, link] of Object.entries(tagLinks)) {
          if (link.tagId === operation.tagId) delete tagLinks[key];
        }
        break;
      }

      case "tag.link": {
        const link: TagLink = {
          spaceId: state.spaceId,
          tagId: operation.body.tagId,
          targetType: operation.body.targetType,
          targetId: operation.body.targetId,
          deletedAt: null,
          revision: 0,
        };
        tagLinks[tagLinkKey(link)] ??= link;
        break;
      }

      case "tag.unlink": {
        delete tagLinks[tagLinkKey(operation.body)];
        break;
      }
    }
  }

  return {
    ...state,
    lists,
    items,
    wishes,
    places,
    placeReviews,
    topics,
    messages,
    events,
    eventParticipants,
    reactions,
    tags,
    tagLinks,
  };
}

/** A fresh id for a row a person just made, matching the server's scheme. */
export const newRowId = uuidv7;
